import { ObjectOrVoid, PathBuilder, ApiRoute, HttpMethod } from '../../../common/api/routes';
import { PrintProviderId } from '../../../printProvider/PrintProvider';
import { AgencyId } from '../../../agency/Agency';
import { StripeAuthRequest } from './StripeAuthRequest';
import { StripeConfiguration } from './StripeConfiguration';
import { CreateStripeChargeBody, StripeChargeDto, StripeChargeErrorResponse } from './StripeCharge';

export interface AuthParams {
    token: string;
}

export interface AuthRequestBody {
    email: string;
    printProviderId: PrintProviderId;
    agencyId: AgencyId;
}

export interface StripeAuthorisationUrlResponse {
    authUrl: URL;
}

export interface StripeClientIdResponse {
    stripeClientId: string | null;
}

export interface SubmitAuthCodeForStripeAccountBody {
    /**
     * Our internal token. We will use this to associate the "StripeConfiguration"."id" to a
     * "PrintProviders"."stripeConfigurationId"
     */
    token: string;
    /**
     * An authorization code you can use in the next call to get an access token for your user. This can only be used
     * once and expires in 5 minutes.
     */
    code: string;
}

export interface SubmitAuthCodeForStripeAccountResponse {
    stripeConfiguration: StripeConfiguration;
}

export interface StripePublishableKeyResponse {
    stripePublishableKey: string;
}

function STRIPE_API_ROOT<T extends ObjectOrVoid>(path: PathBuilder<T>) {
    return path.literal('stripe');
}

export const StripeApi = {
    createStripeCharge: new ApiRoute<StripeChargeDto | StripeChargeErrorResponse, void, CreateStripeChargeBody, void>(
        HttpMethod.Post,
        path => STRIPE_API_ROOT(path).literal('charge'),
    ),
    getStripeAuthRequest: new ApiRoute<StripeAuthRequest, AuthParams, void, void>(HttpMethod.Get, path =>
        STRIPE_API_ROOT(path).literal('authRequest').param('token'),
    ),
    getStripeAuthorisationUrl: new ApiRoute<StripeAuthorisationUrlResponse, AuthParams, void, void>(
        HttpMethod.Get,
        path => STRIPE_API_ROOT(path).literal('authorisation').param('token'),
    ),
    getStripeClientId: new ApiRoute<StripeClientIdResponse, void, void, void>(HttpMethod.Get, path =>
        STRIPE_API_ROOT(path).literal('clientId'),
    ),
    getStripePublishableKey: new ApiRoute<StripePublishableKeyResponse, void, void, void>(HttpMethod.Get, path =>
        STRIPE_API_ROOT(path).literal('publishableKey'),
    ),
    sendStripeAuthRequest: new ApiRoute<void, void, AuthRequestBody, void>(HttpMethod.Post, path =>
        STRIPE_API_ROOT(path).literal('authInvite'),
    ),
    submitAuthCodeForStripeAccount: new ApiRoute<
        SubmitAuthCodeForStripeAccountResponse,
        void,
        SubmitAuthCodeForStripeAccountBody,
        void
    >(HttpMethod.Post, path => STRIPE_API_ROOT(path).literal('authAccount')),
};
